import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import {
  Bot,
  Send,
  Loader2,
  X,
  Minimize2,
  Maximize2,
  Sparkles,
  MessageCircle,
  Copy,
  Check,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const TACO_MASCOT_URL = "https://customer-assets.emergentagent.com/job_ttouchmanage/artifacts/sua7mu0g_Untitled%20design%20%288%29.png";

/**
 * Floating Taco Assistant Widget
 * A compact chat widget that floats on the screen for quick AI assistance
 */
const FloatingTacoWidget = ({ 
  context = null,  // Optional context like "booking_creation" or "quote_writing"
  contextData = {} // Optional data like client info, job details
}) => {
  const { api, user } = useAuth();
  
  // Widget state
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  
  // Chat state
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Copy message to clipboard
  const copyToClipboard = async (text, messageId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(messageId);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  // Check if Taco v2 is enabled
  useEffect(() => {
    const checkEnabled = async () => {
      try {
        const res = await api().get('/taco/v2/health');
        setIsEnabled(res.data.v2_enabled);
      } catch {
        setIsEnabled(false);
      }
    };
    checkEnabled();
  }, [api]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opening
  useEffect(() => {
    if (isOpen && !isMinimized) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, isMinimized]);

  // Quick prompts for booking context
  const quickPrompts = [
    "Write a quote description for this job",
    "What should I include in the job notes?",
    "Suggest a price for this service",
    "Tips for communicating with this client",
  ];

  const sendMessage = async (messageText = null) => {
    const text = messageText || inputMessage.trim();
    if (!text || sending) return;

    // Add user message to UI
    const userMsg = {
      id: `user_${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMsg]);
    setInputMessage('');
    setSending(true);

    try {
      // Build context-aware message
      let contextMessage = text;
      if (context === 'booking_creation' && contextData) {
        const clientInfo = contextData.client 
          ? `\n\nContext: Creating a booking for client "${contextData.client.name}"${contextData.client.service_address ? ` at ${contextData.client.service_address}` : ''}.` 
          : '';
        const packageInfo = contextData.package 
          ? `\nService package: ${contextData.package}` 
          : '';
        contextMessage = text + clientInfo + packageInfo;
      }

      const res = await api().post('/taco/v2/chat', {
        message: contextMessage,
        conversation_id: conversationId,
      });

      // Update conversation ID
      if (res.data.conversation_id) {
        setConversationId(res.data.conversation_id);
      }

      // Add assistant response (API returns 'message' field)
      const assistantMsg = {
        id: `assistant_${Date.now()}`,
        role: 'assistant',
        content: res.data.message || res.data.response || 'No response',
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, assistantMsg]);
    } catch (error) {
      console.error('Taco chat error:', error);
      toast.error('Failed to get response from Taco');
      
      // Remove user message on error
      setMessages(prev => prev.filter(m => m.id !== userMsg.id));
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setConversationId(null);
  };

  // Don't render if Taco is not enabled
  if (!isEnabled) return null;

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 flex items-center justify-center group"
          data-testid="taco-floating-btn"
        >
          <img 
            src={TACO_MASCOT_URL} 
            alt="Taco" 
            className="w-10 h-10 rounded-full object-cover"
          />
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white" />
          
          {/* Tooltip */}
          <span className="absolute right-full mr-3 px-3 py-1.5 bg-zinc-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
            Ask Taco for help
          </span>
        </button>
      )}

      {/* Chat Widget */}
      {isOpen && (
        <div 
          className={cn(
            "fixed z-50 bg-white rounded-2xl shadow-2xl border border-zinc-200 transition-all duration-200",
            isMinimized 
              ? "bottom-6 right-6 w-72 h-14" 
              : "bottom-6 right-6 w-96 h-[500px] max-h-[80vh]"
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100 bg-gradient-to-r from-amber-50 to-orange-50 rounded-t-2xl">
            <div className="flex items-center gap-3">
              <img 
                src={TACO_MASCOT_URL} 
                alt="Taco" 
                className="w-8 h-8 rounded-full object-cover"
              />
              <div>
                <h3 className="font-semibold text-zinc-900 text-sm">Taco Assistant</h3>
                {!isMinimized && (
                  <p className="text-xs text-zinc-500">Here to help with your booking</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="p-1.5 hover:bg-zinc-100 rounded-lg transition-colors"
              >
                {isMinimized ? (
                  <Maximize2 className="w-4 h-4 text-zinc-500" />
                ) : (
                  <Minimize2 className="w-4 h-4 text-zinc-500" />
                )}
              </button>
              <button
                onClick={() => {
                  setIsOpen(false);
                  setIsMinimized(false);
                }}
                className="p-1.5 hover:bg-zinc-100 rounded-lg transition-colors"
              >
                <X className="w-4 h-4 text-zinc-500" />
              </button>
            </div>
          </div>

          {/* Content (hidden when minimized) */}
          {!isMinimized && (
            <>
              {/* Messages */}
              <ScrollArea className="flex-1 h-[calc(100%-140px)] p-4">
                {messages.length === 0 ? (
                  <div className="text-center py-6">
                    <Sparkles className="w-8 h-8 text-amber-400 mx-auto mb-3" />
                    <p className="text-zinc-600 text-sm mb-4">
                      Need help with this booking? Ask me anything!
                    </p>
                    
                    {/* Quick Prompts */}
                    <div className="space-y-2">
                      {quickPrompts.map((prompt, idx) => (
                        <button
                          key={idx}
                          onClick={() => sendMessage(prompt)}
                          className="w-full text-left px-3 py-2 text-sm bg-zinc-50 hover:bg-amber-50 rounded-lg transition-colors text-zinc-700 hover:text-amber-800"
                        >
                          {prompt}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={cn(
                          "flex",
                          msg.role === 'user' ? "justify-end" : "justify-start"
                        )}
                      >
                        <div
                          className={cn(
                            "max-w-[85%] rounded-2xl px-4 py-2.5 relative group",
                            msg.role === 'user'
                              ? "bg-amber-500 text-white rounded-br-sm"
                              : "bg-zinc-100 text-zinc-800 rounded-bl-sm"
                          )}
                        >
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                          
                          {/* Copy button for assistant messages */}
                          {msg.role === 'assistant' && (
                            <button
                              onClick={() => copyToClipboard(msg.content, msg.id)}
                              className="absolute -right-2 -top-2 p-1.5 bg-white border border-zinc-200 rounded-lg shadow-sm opacity-0 group-hover:opacity-100 transition-opacity hover:bg-zinc-50"
                              title="Copy to clipboard"
                              data-testid={`copy-msg-${msg.id}`}
                            >
                              {copiedId === msg.id ? (
                                <Check className="w-3.5 h-3.5 text-green-500" />
                              ) : (
                                <Copy className="w-3.5 h-3.5 text-zinc-500" />
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                    {sending && (
                      <div className="flex justify-start">
                        <div className="bg-zinc-100 rounded-2xl rounded-bl-sm px-4 py-3">
                          <Loader2 className="w-4 h-4 animate-spin text-amber-500" />
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </ScrollArea>

              {/* Input */}
              <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-zinc-100 bg-white rounded-b-2xl">
                <form onSubmit={(e) => { e.preventDefault(); sendMessage(); }} className="flex gap-2">
                  <Input
                    ref={inputRef}
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Ask Taco..."
                    className="flex-1 text-sm"
                    disabled={sending}
                    data-testid="taco-input"
                  />
                  <Button
                    type="submit"
                    size="icon"
                    disabled={!inputMessage.trim() || sending}
                    className="bg-amber-500 hover:bg-amber-600"
                    data-testid="taco-send-btn"
                  >
                    {sending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </form>
                
                {/* Clear chat button */}
                {messages.length > 0 && (
                  <button
                    onClick={clearChat}
                    className="mt-2 text-xs text-zinc-400 hover:text-zinc-600 transition-colors"
                  >
                    Clear conversation
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default FloatingTacoWidget;
